import { $wrapNodeInElement } from '@lexical/utils';
import { $createParagraphNode, $getSelection, $insertNodes, $isRangeSelection, $isRootOrShadowRoot, $isTextNode, COMMAND_PRIORITY_EDITOR, createCommand, $createRangeSelection, $setSelection, $getRoot } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useSelector } from 'react-redux';
import { memo, useEffect } from 'react';
import { $createBlockNode } from './BlockNode';
import setWorkflowActiveInputState from '../../screens/workflows/redux/actions/setWorkflowActiveInputState';

export const INSERT_BLOCK_COMMAND = createCommand('INSERT_BLOCK_COMMAND');

const BlockPlugin = memo(({ id }) => {
  const isLastActiveInput = useSelector((state) => state?.workflow?.activeInputState?.inputId === id);
  const trigger = useSelector((state) => state?.workflow?.activeInputState?.trigger);
  const storedSelection = useSelector((state) => state?.workflow?.activeInputState?.selection);
  const [editor] = useLexicalComposerContext();

  // Replaces text that the user typed in to search for a variable
  const replaceText = (payload) => {
    const selection = editor.getEditorState().read(() => {
      return $getSelection();
    });
    if (selection !== null && $isRangeSelection(selection)) {
      const node = selection.anchor.getNode();
      if ($isTextNode(node)) {
        const text = node.getTextContent();
        const cursorOffset = selection.anchor.offset;
        const replaceStr = payload?.replace || '';

        let startIndex = -1;

        if (replaceStr) {
          const beforeCursor = text.substring(0, cursorOffset).lastIndexOf(replaceStr);
          startIndex = beforeCursor;

          if (startIndex >= 0) {
            const updatedText = text.substring(0, startIndex) + text.substring(startIndex + replaceStr.length);
            console.log('updatedText', updatedText);
            node.setTextContent(updatedText);
          }
        }
      }
    }
  };

  // Restores the cursor position to the position where the user typed in the variable
  const restoreCursorPosition = () => {
    if (storedSelection && storedSelection.text && storedSelection.offset !== undefined) {
      const root = $getRoot();
      const textNodes = [];
      const traverse = (node) => {
        if ($isTextNode(node)) {
          textNodes.push(node);
          return;
        }
        const children = node?.getChildren?.();
        children?.forEach((child) => traverse(child));
      };
      traverse(root);
      const matchingNode = textNodes.find((node) => node.getTextContent() === storedSelection.text);
      if (matchingNode) {
        const rangeSelection = $createRangeSelection();
        rangeSelection.anchor.set(matchingNode.getKey(), storedSelection.offset, 'text');
        rangeSelection.focus.set(matchingNode.getKey(), storedSelection.offset, 'text');
        $setSelection(rangeSelection);
      }
    }
  };

  const insertBlock = (payload) => {
    const blockNode = $createBlockNode(payload);
    $insertNodes([blockNode]);

    if ($isRootOrShadowRoot(blockNode.getParentOrThrow())) {
      $wrapNodeInElement(blockNode, $createParagraphNode).selectEnd();
    }
  };

  // Listens for an insert block command from redux, updates the cursor position,
  // and replaces content with a new block.
  useEffect(() => {
    const listener = (payload) => {
      if (payload?.replace) {
        replaceText(payload);
      }

      if (!payload?.stepId || !payload?.key) {
        return true;
      }

      restoreCursorPosition();
      insertBlock(payload);

      return true;
    };

    return editor.registerCommand(INSERT_BLOCK_COMMAND, listener, COMMAND_PRIORITY_EDITOR);
  }, [editor]);

  useEffect(() => {
    if (isLastActiveInput && trigger) {
      setWorkflowActiveInputState({ trigger: null });
      editor.dispatchCommand(INSERT_BLOCK_COMMAND, trigger);
    }
  }, [isLastActiveInput, trigger]);

  return null;
});

BlockPlugin.displayName = 'BlockPlugin';

export default BlockPlugin;
